import React, { useRef, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import VouchersAPI from '../../../api/v3/vouchers-api-v3';
import { buildVoucherPayload, initialVoucherForm, validateVoucherForm, voucherCreateError } from './voucher-create-form';

export default function VoucherCreateModal({ isOpen, onClose, onCreated }) {
  const [form, setForm] = useState(initialVoucherForm);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);
  const update = (key, value) => {
    setForm(previous => ({ ...previous, [key]: value }));
    setErrors(previous => ({ ...previous, [key]: undefined }));
  };
  const feedback = key => errors[key] && <div className="text-danger small mt-1">{errors[key]}</div>;

  async function submit(event) {
    event.preventDefault();
    if (submitting.current) return;
    const nextErrors = validateVoucherForm(form);
    setErrors(nextErrors);
    setError('');
    if (Object.keys(nextErrors).length) return;
    submitting.current = true;
    setSaving(true);
    try {
      const response = await VouchersAPI.create(buildVoucherPayload(form));
      const item = response?.item;
      if (!item?.id) throw new Error('Respons penyimpanan tidak lengkap. Periksa daftar voucher sebelum mencoba lagi.');
      window.alert(`Voucher ${item.code} berhasil dibuat.`);
      onCreated(item);
    } catch (e) {
      setError(voucherCreateError(e));
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  }

  const numberField = (key, label, { required = false, max = '9999999999.99', step = '0.01', min = '0' } = {}) => (
    <div className="form-group col-md-6">
      <label htmlFor={`voucher-create-${key}`}>{label}{required ? ' *' : ''}</label>
      <input id={`voucher-create-${key}`} type="number" className="form-control" min={min} max={max} step={step}
        value={form[key]} onChange={event => update(key, event.target.value)} required={required} aria-invalid={Boolean(errors[key])} />
      {feedback(key)}
    </div>
  );

  return (
    <Modal isOpen={isOpen} toggle={saving ? undefined : onClose} size="lg" centered scrollable backdrop="static" keyboard={!saving}>
        <ModalHeader toggle={saving ? undefined : onClose}>Create Voucher</ModalHeader>
        <ModalBody>
          <form id="bagspa-create-voucher-form" onSubmit={submit} noValidate>
          {error && <div className="alert alert-danger" role="alert" style={{ whiteSpace: 'pre-wrap' }}>{error}</div>}
          {Object.keys(errors).some(key => errors[key]) && <div className="alert alert-warning" role="alert">Periksa kembali kolom yang ditandai.</div>}
          <fieldset disabled={saving} style={{ minWidth: 0 }}>
            <div className="row">
              <div className="col-md-7">
                <div className="form-group">
                  <label htmlFor="voucher-create-code">Code *</label>
                  <input id="voucher-create-code" className="form-control" autoFocus required maxLength={255}
                    value={form.code} onChange={event => update('code', event.target.value)} aria-invalid={Boolean(errors.code)} />
                  {feedback('code')}
                </div>
                <div className="form-group">
                  <label htmlFor="voucher-create-type">Discount Type</label>
                  <select id="voucher-create-type" className="form-control" value={form.discount_type} onChange={event => update('discount_type', event.target.value)}>
                    <option value="fixed_amount">Fixed Amount (Rp)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                  {feedback('discount_type')}
                </div>
                <div className="row">
                  {numberField('discount_value', form.discount_type === 'percentage' ? 'Discount Value (%)' : 'Discount Value (Rp)', { required: true, min: form.discount_type === 'percentage' ? '1' : '0.01', max: form.discount_type === 'percentage' ? '100' : '999999.99' })}
                  {numberField('maximum_discount_value', 'Maximum Discount (Rp)')}
                  {numberField('minimum_transaction_value', 'Minimum Transaction (Rp)')}
                  {numberField('max_redemption_per_user', 'Maximum Redemption / Customer', { required: true, min: '1', max: '2147483647', step: '1' })}
                </div>
                <div className="form-group">
                  <label htmlFor="voucher-create-expiry">Expiration Date (WIB)</label>
                  <input id="voucher-create-expiry" type="datetime-local" className="form-control" value={form.expiration_date}
                    onChange={event => update('expiration_date', event.target.value)} aria-invalid={Boolean(errors.expiration_date)} />
                  <small className="text-muted">Kosongkan jika tanpa batas waktu. Waktu selalu menggunakan WIB (UTC+7).</small>
                  {feedback('expiration_date')}
                </div>
              </div>
              <div className="col-md-5">
                <div className="d-flex flex-wrap mb-3">
                  <div className="form-check mr-4">
                    <input id="voucher-create-active" type="checkbox" className="form-check-input" checked={form.active} onChange={event => update('active', event.target.checked)} />
                    <label className="form-check-label" htmlFor="voucher-create-active">Active</label>
                  </div>
                  <div className="form-check">
                    <input id="voucher-create-promoted" type="checkbox" className="form-check-input" checked={form.is_promoted} onChange={event => update('is_promoted', event.target.checked)} />
                    <label className="form-check-label" htmlFor="voucher-create-promoted">Promoted</label>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="voucher-create-promo">Promotional Text{form.is_promoted ? ' *' : ''}</label>
                  <textarea id="voucher-create-promo" className="form-control" rows={3} maxLength={255} value={form.promotional_text}
                    onChange={event => update('promotional_text', event.target.value)} aria-invalid={Boolean(errors.promotional_text)} />
                  <small className="text-muted">Wajib diisi jika Promoted dipilih.</small>
                  {feedback('promotional_text')}
                </div>
                <div className="form-group">
                  <label htmlFor="voucher-create-scope">Applies To</label>
                  <select id="voucher-create-scope" className="form-control" value={form.applies_to_scope} onChange={event => update('applies_to_scope', event.target.value)}>
                    <option value="all_items">All Items</option>
                    <option value="product_categories">Product Categories</option>
                    <option value="order_categories">Order Categories / Treatments</option>
                  </select>
                  {feedback('applies_to_scope')}
                </div>
                {form.applies_to_scope === 'product_categories' && (
                  <div className="form-group">
                    <label htmlFor="voucher-create-products">Product Category Codes *</label>
                    <textarea id="voucher-create-products" className="form-control" rows={3} placeholder="BG, WL" value={form.product_category_codes}
                      onChange={event => update('product_category_codes', event.target.value)} aria-invalid={Boolean(errors.product_category_codes)} />
                    <small className="text-muted">Gunakan kode dari Taxonomy → Categories. Pisahkan beberapa kode dengan koma.</small>
                    {feedback('product_category_codes')}
                  </div>
                )}
                {form.applies_to_scope === 'order_categories' && (
                  <div className="form-group">
                    <label htmlFor="voucher-create-treatments">Treatment Codes *</label>
                    <textarea id="voucher-create-treatments" className="form-control" rows={3} placeholder="FULL_TREATMENT-BAG-LEATHER-S" value={form.order_category_codes}
                      onChange={event => update('order_category_codes', event.target.value)} aria-invalid={Boolean(errors.order_category_codes)} />
                    <small className="text-muted">Gunakan kode dari Taxonomy → Order Categories. Pisahkan beberapa kode dengan koma.</small>
                    {feedback('order_category_codes')}
                  </div>
                )}
              </div>
            </div>
            <p className="text-muted small mb-0">Sesuai aturan Bag Spa saat ini, setiap kode voucher hanya dapat digunakan satu kali secara keseluruhan. Batas per customer tidak menambah batas tersebut.</p>
          </fieldset>
          </form>
        </ModalBody>
        <ModalFooter>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="bagspa-create-voucher-form" className="btn btn-primary" disabled={saving}>{saving ? 'Menyimpan…' : 'Save Voucher'}</button>
        </ModalFooter>
    </Modal>
  );
}
